import React from 'react';
import PropTypes from 'prop-types';
import { Col, Grid, Row } from 'react-flexbox-grid';
import { path } from 'ramda';

import Paper from 'components/Paper';
import { FormattedMessage } from 'react-intl';
import { colors } from 'styles/provider';

import EditableV2 from 'routes/Documents/containers/EditableV2';
import { getAllCommitsData, getLatestUnfinishedCommit } from 'lib/documentHandling';

import StepperWrapper from './StepperWrapper';
import CommonHeader from 'routes/Documents/components/CommonHeader';
import HistoryContainer from 'containers/HistoryContainer';
import historyMapper, { sorter } from './historyMapper';
import FormProvider from 'routes/Documents/containers/FormProvider';
import { hasDiaryBeenFilled } from '../utils';
import AnswerArea from './AnswerArea';

const titleStyle = {
  color: colors.clrDarkGray,
  fontWeight: 700,
  fontSize: '16px',
  padding: '1rem',
};

const Form = props => {
  return (
    <FormProvider {...props}>
      <StepperWrapper
        editingDocumentId={props.documentId}
        editedCommit={props.editedCommit}
        hasDiaryBeenFilled={props.hasDiaryBeenFilled}
        documentData={props.documentData}
      />
    </FormProvider>
  );
};

const OnlineRehabilitation = ({ names, documents, editingDocument, onStartEdit, onCreate }) => {
  const rehabDocs = documents.filter(d => d.documentType === 'onlineRehabilitation');
  const hasStartedDocs = rehabDocs.find(d => {
    const contents = getAllCommitsData(d);
    return !(contents.cancelled || contents.finished);
  });

  const diaryFilled = hasDiaryBeenFilled(documents, path(['createDate'], hasStartedDocs));

  const [viewingID, setViewingID] = React.useState(null); // null or string
  const viewingDoc = rehabDocs.find(d => d.documentId === viewingID);
  return (
    <Paper topMargin={false}>
      {!(editingDocument || viewingID) && (
        <div>
          <Grid fluid>
            <CommonHeader
              names={[names[0]]}
              localeId="onlineRehabilitation.title"
              infoText="onlineRehabilitation.infoText"
              addNewText="onlineRehabilitation.addNew"
              mode="create"
              editing={editingDocument}
              startEdit={onCreate}
              disableEditStartButton={!!hasStartedDocs}
            />
          </Grid>
          <HistoryContainer
            documents={rehabDocs}
            emptyText="rehabilitation.emptyText"
            sorter={sorter}
            mapper={historyMapper(names[0], onStartEdit, setViewingID)}
          />
        </div>
      )}

      {viewingID && (
        <Grid fluid>
          <Row middle="xs" style={titleStyle}>
            <Col xs={6}>
              <FormattedMessage id="onlineRehabilitation.title" />
            </Col>
            <Col xs={6}>
              <Row end="xs">
                <Col>
                  <AnswerArea doc={hasStartedDocs} />
                </Col>
              </Row>
            </Col>
          </Row>
          <StepperWrapper
            viewingId={viewingID}
            hasDiaryBeenFilled={diaryFilled}
            documentData={getAllCommitsData(viewingDoc)}
          />
        </Grid>
      )}

      {editingDocument && (
        <Grid fluid>
          <Row middle="xs" style={titleStyle}>
            <Col xs={6}>
              <FormattedMessage id="onlineRehabilitation.title" />
            </Col>
            <Col xs={6}>
              <Row end="xs">
                <Col>
                  <AnswerArea doc={hasStartedDocs} />
                </Col>
              </Row>
            </Col>
          </Row>
          <EditableV2
            documentId={editingDocument.documentId}
            moduleName={names[0]}
            editedCommit={getLatestUnfinishedCommit(editingDocument)}
          >
            <Form documentData={getAllCommitsData(editingDocument)} hasDiaryBeenFilled={diaryFilled} />
          </EditableV2>
        </Grid>
      )}
    </Paper>
  );
};

OnlineRehabilitation.propTypes = {
  names: PropTypes.array,
  documents: PropTypes.array,
  onStartEdit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  editingDocument: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default OnlineRehabilitation;
